
import { Task } from '@/types/tasks';
import { useState } from 'react';

/**
 * Hook to handle task state updates during drag operations
 */
export const useTaskStateUpdates = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  
  /**
   * Safely update an item in an array
   */
  const updateItemInArray = <T extends { id: string }>(array: T[], id: string, updates: Partial<T>): T[] => {
    return array.map(item => 
      item.id === id ? { ...item, ...updates } : item
    );
  };

  /**
   * Update the task status (completed/active) in the local arrays
   */
  const updateTaskStatus = (
    tasks: {
      active: Task[];
      completed: Task[];
    },
    taskId: string, 
    isCompleted: boolean,
    setActiveTasks: (tasks: Task[]) => void,
    setCompletedTasks: (tasks: Task[]) => void,
  ) => {
    setIsUpdating(true);
    
    // Find the task in either array
    const task = isCompleted 
      ? tasks.active.find(t => t.id === taskId)
      : tasks.completed.find(t => t.id === taskId);
    
    if (!task) {
      setIsUpdating(false);
      return;
    }
    
    // Update the task status
    const updatedTask = { ...task, isCompleted: isCompleted };
    
    // Remove from source array
    if (isCompleted) {
      setActiveTasks(tasks.active.filter(t => t.id !== taskId));
      setCompletedTasks([...tasks.completed, updatedTask]);
    } else {
      setCompletedTasks(tasks.completed.filter(t => t.id !== taskId));
      setActiveTasks([...tasks.active, updatedTask]);
    }
    
    setIsUpdating(false);
  };
  
  return {
    isUpdating,
    updateItemInArray,
    updateTaskStatus
  };
};
